import React, { FC, useState } from 'react';
import { ParamsProps } from '../../../../../common/components/ParamsProvider';
import { getWalletAwardList, updatedWalletAwardStatus } from '../../../network/awardCrud';
import { useDataProvider } from 'src/app/common/utils';
import {
  AWARD_STATUS_OPERATION,
  AwardStatusEnum,
  UPDATE_AWARD_STATUS,
  WalletAwardListParam,
  WalletAwardListRes,
} from '../../../types/award-type';
import { useDispatch } from 'react-redux';
import WalletAwardList from './components/WalletAwardList';
import PruFilter, { PruFilterItemType } from 'src/app/common/components/PruTable/PruFilter';
import { useIntl } from 'react-intl';
import { useServerLocale } from 'src/app/i18n';
import _ from 'lodash';
import { AlertType, appendAlertItem } from '@pruforce/common-adminweb-sdk';

type AwardListingPageProps = {} & ParamsProps;

const initialState: WalletAwardListParam = {
  pageSize: 5,
  page: 1,
  awardId: '',
  awardNameZh: '',
  awardNameEn: '',
  status: '',
};

const paramsInitiator = (initialParams?: Record<string, string>): WalletAwardListParam => {
  return initialParams
    ? {
        page: 1,
        pageSize: 5,
        awardId: initialParams.awardId ?? undefined,
        awardNameZh: initialParams.awardNameZh ?? undefined,
        awardNameEn: initialParams.awardNameEn ?? undefined,
        status: initialParams.status ?? undefined,
      }
    : initialState;
};

export const walletAwardFilterKeys = [];

const AwardListingPage: FC<AwardListingPageProps> = ({ initialParams, onChangeQueryParams }) => {
  const locale = useServerLocale();
  const dispatch = useDispatch();
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });

  const [filterState, setFilterState] = useState<WalletAwardListParam>(paramsInitiator(initialParams));

  const [listRes, setListRes] = useState<WalletAwardListRes>();
  const { isLoading, refreshData } = useDataProvider<WalletAwardListRes>(
    async () => {
      try {
        if (onChangeQueryParams) {
          onChangeQueryParams(filterState);
        }
        return await getWalletAwardList(filterState, dispatch);
      } catch (err) {
        console.error(err);
      }
    },
    setListRes,
    false,
  );

  const onUpdatedAwardStatus = async (id: string, param: UPDATE_AWARD_STATUS, operation: AWARD_STATUS_OPERATION) => {
    try {
      const result = await updatedWalletAwardStatus(id, param, operation);
      const update = result?.data;
      if (!update.awardId) return;
      setListRes((prev) => {
        if (!prev?.data) return prev;
        return {
          ...prev,
          data: {
            ...prev.data,
            items: prev.data.items.map((item: any) =>
              item.awardId === update.awardId ? { ...item, status: update.status } : item,
            ),
          },
        };
      });
    } catch (err: any) {
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.ERROR,
            title: 'Error',
            content: `${err?.message}`,
          },
        ]),
      );
    }
  };

  return (
    <>
      <PruFilter
        title={Translation('walletAward.filter')}
        itemDef={[
          {
            type: PruFilterItemType.FREE_TEXT,
            style: filterWidth,
            field: 'awardId',
            initialValue: filterState.awardId,
            displayName: Translation('walletAward.common.awardId'),
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            style: filterWidth,
            field: 'awardNameZh',
            initialValue: filterState.awardNameZh,
            displayName: Translation('walletAward.common.awardNameZh'),
          },
          {
            type: PruFilterItemType.FREE_TEXT,
            style: filterWidth,
            field: 'awardNameEn',
            initialValue: filterState.awardNameEn,
            displayName: Translation('walletAward.common.awardNameEn'),
          },
          {
            type: PruFilterItemType.DROPDOWN,
            field: 'status',
            displayName: Translation('walletAward.common.status'),
            initialValue: filterState.status,
            choices: [
              { displayName: Translation('component.status.all'), value: '' },
              ..._.map(AwardStatusEnum, (option: string) => ({
                displayName: Translation(`walletAward.common.status.option.${option}`),
                value: option,
              })),
            ],
          },
          {
            type: PruFilterItemType.DATE_RANGE,
            fieldFrom: 'redemptionStartDate',
            fieldTo: 'redemptionEndDate',
            initialDateFrom: filterState.redemptionStartDate,
            initialDateTo: filterState.redemptionEndDate,
            displayName: Translation('walletAward.common.redemptionDate'),
          },
          {
            type: PruFilterItemType.DATE_RANGE,
            fieldFrom: 'awardAvailabilityStartDate',
            fieldTo: 'awardAvailabilityEndDate',
            initialDateFrom: filterState.awardAvailabilityStartDate,
            initialDateTo: filterState.awardAvailabilityEndDate,
            displayName: Translation('walletAward.common.validityDate'),
          },
        ]}
        onChangeFilter={(data) => {
          setFilterState({
            ...filterState,
            awardId: data.awardId,
            awardNameZh: data.awardNameZh,
            awardNameEn: data.awardNameEn,
            status: data.status,
            redemptionStartDate: data.redemptionStartDate,
            redemptionEndDate: data.redemptionEndDate,
            awardAvailabilityStartDate: data.awardAvailabilityStartDate,
            awardAvailabilityEndDate: data.awardAvailabilityEndDate,
          });
        }}
        fetchData={refreshData}
      />
      <WalletAwardList
        isLoading={isLoading}
        listRes={listRes}
        onRefresh={refreshData}
        onChangePage={(page, rowsPerPage) => {
          setFilterState({
            ...filterState,
            page,
            pageSize: rowsPerPage,
          });
          refreshData();
        }}
        onUpdatedAwardStatus={onUpdatedAwardStatus}
      />
    </>
  );
};

export default AwardListingPage;

const filterWidth = { width: 200 };
